import React, { useState, useEffect } from 'react'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { ConnectedRouter } from 'connected-react-router'
import { connectResource, tokenPresentOnMount } from './common/utils/resource'
import { compose } from 'redux'
import Loader from './common/layout/components/Loader'
import AppContainer from './common/layout/AppContainer'
import { hot } from 'react-hot-loader'
import { fetchCrossBrowserCSS, isBadBrowser, renderBadBrowserMessage } from './common/crossBrowser'
import { withLDProvider, useLDClient } from 'launchdarkly-react-client-sdk'
import { ld_client_id } from './init'
import { getLDUser, privateLDAttributeNames } from '../helpers/ldHelpers'
import { tokenService } from '@app/auth/tokenService'

fetchCrossBrowserCSS()

// Separate inner component that uses Redux hooks
const AppContent = ({ user }) => {
  const [loading, setLoading] = useState(true)
  const [ldUserId, setLdUserId] = useState(null)
  const dispatch = useDispatch() // Now this is inside the Provider
  const ldClient = useLDClient()

  // Access loaded token from redux
  const token = useSelector(state => state.resource?.session?.data?.token)

  const userId = user?.data?.id

  useEffect(() => {
    const readToken = async () => {
      const token = await tokenService.getTokenAsync()
      if (token) {
        dispatch(tokenPresentOnMount(token))
      } else {
        dispatch(tokenPresentOnMount(null))
      }
      setLoading(false)
    }

    readToken()
  }, [dispatch])

  // Update LaunchDarkly user when our user data changes
  useEffect(() => {
    if (user?.data?.id && user?.data?.id !== ldUserId && ldClient) {
      ldClient.identify(getLDUser(user.data))
      setLdUserId(user.data.id)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, ldUserId, ldClient])

  if (loading || (token && !user.data)) {
    return <Loader />
  }

  return <AppContainer />
}

// Main provider component that doesn't use Redux hooks
const AppProvider = ({ store, history, user }) => {
  // Handle browser compatibility - can be done outside Redux
  if (isBadBrowser) {
    return renderBadBrowserMessage
  }

  return (
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <AppContent user={user} />
      </ConnectedRouter>
    </Provider>
  )
}

// The enhanced component with all the HOCs
const EnhancedAppProvider = compose(
  connectResource(
    { namespace: 'user', endpoint: 'user', list: true, prefetch: false }
  ),
  withLDProvider({
    clientSideID: ld_client_id,
    user: {
      key: 'unidentified_init_user',
      name: 'init_user',
      lender_name: 'init_lender',
      email: 'init_user@reggora.com',
      type: 'LENDER_USER',
      privateAttributeNames: privateLDAttributeNames,
    },
    options: { privateAttributeNames: privateLDAttributeNames },
  })
)(AppProvider)

// Export the hot-reloadable version
export default hot(module)(EnhancedAppProvider)
